import * as React from 'react';
import cn from 'classnames';
import FieldGroup from 'src/components/FieldGroup';
import ILoginStepProps from 'src/layouts/unauthorized/Login/components/ILoginStepProps';
import validateEmail from 'src/app/data/common/utils/validateEmail';
import styles from 'src/layouts/unauthorized/Login/Login.css';
import Button, { ButtonType } from 'src/components/Button';
import { connect } from 'react-redux';
import actions from 'src/app/redux/store/actions';
import { RouteComponentProps, withRouter } from 'react-router';
import { AuthType, IAuthSchema } from 'src/app/redux/modules/auth/interfaces/IAuthData';
import ErrorMessage from 'src/components/ErrorMessage';
import validatePassword from 'src/app/data/common/utils/validatePassword';
import ROUTE_PATHS from 'src/routing/paths';
import getUrlParamValue, { updateUrlParamsValue } from 'src/app/data/common/utils/queryParamsAccessor';
import { ERROR_CODE_NO_USER_ROLE, ERROR_CODE_OBJECT_NOT_FOUND } from 'src/app/data/common/utils/errorUtils';
import { Link } from 'react-router-dom';
import Separator from 'src/components/Separator';
import ImgButton from 'src/components/ImgButton';
import * as H from 'history';
import TranslateMessage from 'src/i18n/TranslateMessage';
import { injectIntl, WrappedComponentProps } from 'react-intl';
import ReCAPTCHA from 'react-google-recaptcha';
import { selectors } from 'src/app/redux';
import { IAppState } from 'src/app/redux/store/reducers';
import IRestError from 'src/app/data/common/interfaces/IRestError';
import { PROD_HOST_NAME } from 'src/app/data/common/constants';

const PARAM_ROLE = 'role';
const VALID_COMPANY_NAMES = ['starbucks', 'starbuck\'s', 'starbucks coffee company'];

interface IEmailStepProps extends ILoginStepProps, RouteComponentProps, WrappedComponentProps {
  clearErrors: () => void;

  fetchAuthSchemas: (email: string) => Promise<IAuthSchema[]>;
  updateAuthSchemas: (schemas: IAuthSchema[]) => void;
  logIn: (email: string, password: string, roleID?: string, captchaToken?: string | null, onFailure?: (error: any) => void) => void;
  location: H.Location<{ from: H.Location }>;
  errors: IRestError[] | null;
}

interface IEmailStepState {
  email: string;
  password: string;
  loading: boolean;
  error: string;
  authSchemas: IAuthSchema[];

  showPasswordInput: boolean;
  showSsoOptions: boolean;
  showNextButton: boolean;
  lastSubmittedEmail: string;
  passwordShown: boolean;
  reCaptchaTokenExists: boolean;
  companyName: string;
  showCompanyName: boolean;
}

class EmailStep extends React.Component<IEmailStepProps, IEmailStepState> {

  public state: IEmailStepState = {
    authSchemas: [],
    email: '',
    password: '',
    loading: false,
    error: '',
    showPasswordInput: false,
    showSsoOptions: true,
    showNextButton: true,
    lastSubmittedEmail: '',
    passwordShown: false,
    reCaptchaTokenExists: false,
    companyName: '',
    showCompanyName: false,
  };

  private passwordInput: HTMLElement;
  private reCaptchaRef: React.RefObject<ReCAPTCHA> = React.createRef();

  public componentDidUpdate(prevProps: any, prevState: Readonly<IEmailStepState>): void {
    if (!prevState.showPasswordInput && this.state.showPasswordInput && this.passwordInput) {
      // autoFocus prop is not working
      this.passwordInput.focus();
    }
  }

  private handleChange = (value: string) => {
    this.setState({ reCaptchaTokenExists: !!value });
  }

  private showCaptcha = () => {
    return this.props.errors && this.props.errors[0].attempts && this.props.errors[0].attempts <= 2;
  }

  private renderCaptcha = () => {
    const host = window.location.hostname;
    const siteKey = (host === PROD_HOST_NAME) ? process.env.REACT_APP_RECAPTCHA_SITE_KEY_FOR_PROD : process.env.REACT_APP_RECAPTCHA_SITE_KEY_FOR_OTHER_ENVS;

    return (
      this.showCaptcha() && siteKey ?
        <ReCAPTCHA
          style={{ display: "inline-block" }}
          theme="dark"
          ref={this.reCaptchaRef}
          sitekey={siteKey}
          onChange={this.handleChange}
        />
        :
        <></>
    );
  }

  private clearError() {
    if (this.state.error) {
      this.setState({
        error: '',
      });
    }
  }

  private showCompanyInputField = () => {
    this.clearError();
    this.setState({
      showCompanyName: true,
      email: '',
    });
  };

  public render() {
    const {
      authSchemas,
      email,
      error,
      loading,
      password,
      showPasswordInput,
      showSsoOptions,
      showNextButton,
      companyName,
      showCompanyName,
    } = this.state;
    const ssoSchemas = authSchemas.filter(s => !!s.id);
    const { intl } = this.props;

    return (
      <>
        {
          !showCompanyName &&
          <FieldGroup
          id="email"
          csvProtected={true}
          className={styles.emailInput}
          disabled={this.props.disabled || loading}
          label={intl.formatMessage({ id: 'Login.Email' })}
          type="email"
          placeholder={intl.formatMessage({ id: 'Login.EmailHint' })}
          value={email}
          onChange={this.onChangeEmail}
          onKeyDown={this.onEmailKeyDown}
          autoFocus={!showPasswordInput}
          name={'email'}
          autoComplete="on"
          aria-required={true}
          />
        }
        {
          showCompanyName &&
          <FieldGroup
            id='email'
            csvProtected={true}
            className={styles.emailInput}
            disabled={this.props.disabled || loading}
            label={intl.formatMessage({ id: 'MursionPortal.Label.OrganizationName' })}
            type='text'
            placeholder={intl.formatMessage({ id: 'MursionPortal.OrganizationName.Placeholder' })}
            value={companyName}
            onChange={this.onChangeCompanyName}
            onKeyDown={this.onEmailKeyDown}
            autoFocus={showCompanyName}
            autoComplete='on'
            aria-required={true}
          />
        }
        <FieldGroup
          id="password"
          label={intl.formatMessage({ id: 'Login.Password' })}
          className={cn(styles.passwordFormGroup, !showPasswordInput && styles.hiddenPassword)}
          disabled={this.props.disabled}
          type={this.getTypeOfInputField()}
          placeholder="*******"
          value={password}
          name={'password'}
          autoComplete="on"
          autoFocus={showPasswordInput}
          tabIndex={!showPasswordInput ? -1 : undefined}
          inputRef={this.setPasswordInputRef}
          onChange={this.onChangePassword}
          onKeyDown={this.onPasswordKeyDown}
          aria-required={true}
          showPasswordToggle={true}
          onTogglePassword={this.onTogglePassword}
        />
        {
          showPasswordInput ? (
            <>
              {this.renderCaptcha()}
               <Button
                className={styles.loginBtn}
                btnType={ButtonType.LIGHT_BLUE}
                disabled={!this.isValid() || this.props.disabled}
                type="button"
                onClick={this.submitLogin}
              >
                {TranslateMessage('Login.Login')}
              </Button> 
              <div className={styles.loginLinksContainer}>
                <Link to={ROUTE_PATHS.PASSWORD_RESTORE}>{TranslateMessage('Login.ForgotPassword')}</Link>
              </div>
            </>
            ) : showNextButton && (
              <Button
                className={styles.nextBtn}
                btnType={ButtonType.LIGHT_BLUE}
                disabled={!this.isValid() || loading}
                type="button"
                onClick={this.submitNext}
              >
                {TranslateMessage('Login.Next')}
              </Button>
            )
          }
        {
          (!showCompanyName && !showPasswordInput) &&
          <div className={styles.noLoginText}>
            <button
              className={styles.noLoginButton}
              onClick={this.showCompanyInputField}
              aria-label={intl.formatMessage({ id: 'MursionPortal.DontHaveEmail.Text' })}
              type='button'
            >
              {TranslateMessage('MursionPortal.DontHaveEmail.Text')}
            </button>
          </div>
        }
        {
          showSsoOptions && ssoSchemas.length > 0 && (
            <>
              <Separator/>
              <div className={styles.signInOptions}>
                <label>Sign In with</label>
                {
                  ssoSchemas.map(schema => {
                    return (
                      <ImgButton
                        imgSrc={schema.icon || ''}
                        key={schema.id}
                        label={schema.name}
                        onClick={this.ssoLogin.bind(this, schema)}
                      />
                    );
                  })
                }
              </div>
            </>
          )
        }
        <ErrorMessage className={styles.error} message={error} />
      </>
    );
  }

  private setPasswordInputRef = (el: HTMLElement) => {
    this.passwordInput = el;
  };

  private onChangeCompanyName = (event: React.FormEvent<HTMLFormElement>) => {
    this.clearError();
    this.setState({ companyName: (event.target as HTMLInputElement).value });
  };

  private onChangeEmail = (e: React.FormEvent<HTMLFormElement>) => {
    this.props.clearErrors();
    this.clearError();

    const { authSchemas, lastSubmittedEmail } = this.state;
    const email = (e.target as HTMLInputElement).value;
    const emailNotChanged = !!lastSubmittedEmail && lastSubmittedEmail === email;
    const showPasswordInput = emailNotChanged
      && (!authSchemas || authSchemas.length === 0 || authSchemas.some(s => s.schema === AuthType.PASSWORD));
  
    this.setState({
      email,
      password: '',
      showPasswordInput,
      showSsoOptions: emailNotChanged,
      showNextButton: !emailNotChanged,
    });
  };

  private onChangePassword = (formEvent: React.FormEvent<HTMLFormElement>) => {
    this.setState({
      password: (formEvent.target as HTMLInputElement).value,
    });
  };

  private onEmailKeyDown = async (keyboardEvent: React.KeyboardEvent<HTMLElement>): Promise<void> => {
    // 'keypress' event misbehaves on mobile, so we track 'Enter' key via 'keydown' event
    if (this.isValid() && keyboardEvent.key === 'Enter') {
      keyboardEvent.preventDefault();
      keyboardEvent.stopPropagation();
      await this.submitNext();
    }
  };

  private onPasswordKeyDown = async (keyboardEvent: React.KeyboardEvent<HTMLElement>): Promise<void> => {
    // 'keypress' event misbehaves on mobile, so we track 'Enter' key via 'keydown' event
    if (this.isValid() && keyboardEvent.key === 'Enter') {
      keyboardEvent.preventDefault();
      keyboardEvent.stopPropagation();
      await this.submitLogin();
    }
  };

  private onTogglePassword = (): void => {
    this.setState({passwordShown:!this.state.passwordShown});
  };

  private getTypeOfInputField = (): string => {
     return this.state.passwordShown? "text" : "password";
  };

  private isValid = () => {
    const { email, password, showPasswordInput, reCaptchaTokenExists, showCompanyName, companyName } = this.state;

    return !validateEmail(email) && (!showPasswordInput || (!validatePassword(password))) && !(this.showCaptcha() && !reCaptchaTokenExists) || (showCompanyName && companyName.length);
  };

  private submitLogin = async () => {
    const { email, password } = this.state;
    const { from } = this.props.location && this.props.location.state || { from: { pathname: ROUTE_PATHS.HOME } };
    const roleID = from ? getUrlParamValue(PARAM_ROLE, from) : undefined;

    if (!email || !password) {
      return;
    }

    const captchaToken = (this.state.reCaptchaTokenExists && this.reCaptchaRef.current)? this.reCaptchaRef.current.getValue() : '';

    this.props.logIn(email, password, roleID, captchaToken, (e) => {
      if (roleID && (e.code === ERROR_CODE_OBJECT_NOT_FOUND || e.code === ERROR_CODE_NO_USER_ROLE)) {
        updateUrlParamsValue({ [PARAM_ROLE]: undefined }, from, this.props.history);
      }
    });
    
    if (captchaToken && this.reCaptchaRef.current) {
      this.setState({reCaptchaTokenExists: false});
      this.reCaptchaRef.current.reset();
    }
  };

  private getRedirectLinkForStarbucks() {
    return window.location.protocol + '//' + window.location.host + '/sso/auth/login/mursion-starbucks';
  }

    private submitNext = async () => {
      const { email, lastSubmittedEmail, showCompanyName, companyName } = this.state;
      const { intl } = this.props;
  
      this.props.clearErrors();

      if (showCompanyName) {
        if (VALID_COMPANY_NAMES.includes(companyName.toLocaleLowerCase())) {
          window.open(this.getRedirectLinkForStarbucks(), '_self');
        } else {
          this.setState({
            error: intl.formatMessage({ id: 'MursionPortal.InvalidCompanyName.Error' }),
            showCompanyName: false,
            companyName: '',
          });
        }

        return;
      }
  
      if (!!lastSubmittedEmail && lastSubmittedEmail === email) {
        this.setState({
          showPasswordInput: true,
        });
        return;
      }
  
      this.setState({
        loading: true,
      });
  
      try {
        const authSchemas = await this.props.fetchAuthSchemas(email);
        const showPasswordInput = authSchemas.length === 0 || authSchemas.some(s => s.schema === AuthType.PASSWORD);
        const showSsoOptions = authSchemas.some(s => s.id);
  
        this.props.updateAuthSchemas(authSchemas);
  
        this.setState({
          authSchemas,
          lastSubmittedEmail: email,
          loading: false,
          showPasswordInput,
          showSsoOptions,
          showNextButton: false,
        });
  
        if (showPasswordInput) {
          return;
        }
  
        let { currentSchema } = this.props.data;
  
        if (authSchemas.length === 1) {
          currentSchema = authSchemas[0];
        }
  
        this.props.onSubmit({
          ...this.props.data,
          email,
          currentSchema,
        });
      } catch (e) {
        this.setState({
          error: e.message,
          loading: false,
        });
      }
  };

  private ssoLogin = (currentSchema: IAuthSchema, mouseEvent: React.MouseEvent<HTMLElement>) => {
    mouseEvent.preventDefault();

    const { email } = this.state;

    this.props.onSubmit({
      ...this.props.data,
      email,
      currentSchema,
    });
  };
}

export default injectIntl(withRouter(connect(
  (state: IAppState) => ({
    errors: selectors.auth.getAuthErrors(state),
  }),
  {
    clearErrors: actions.auth.clearErrors,
    fetchAuthSchemas: actions.auth.fetchAuthSchemas,
    updateAuthSchemas: actions.auth.updateAuthSchemas,
    logIn: actions.auth.logIn,
  },
)(EmailStep)));
